import { FieldExpression, NodeExpression, term, LuceneExpression, RangeExpression } from 'lucene';
import moment from 'moment';

import {
	DateRangeFilter, DimensionFilter, ShipmentDimensionFilter,
	NumberFilter, OrderNumberFilter, TextFilter, ExactNumberFilter,
	FilterType,
	ProducedDimensionFilter,
} from '../models/ag-grid';
import { DATE_FORMAT_PATTERN, DATE_FORMAT_PATTERN_ZONE } from '../models/filter-formats';

const FILTER_COLUMN_MAPPING = new Map<string, keyof OrderNumberFilter>([
	['customerOrderNumber', 'reference'],
	['customerOrderItemNumber', 'item'],
	['supplierOrderNumber', 'reference'],
	['supplierOrderItemNumber', 'item'],
	['millOrderNumber', 'reference'],
	['millOrderItemNumber', 'item'],
	['millOrderNumber', 'reference'],
	['millOrderItemNumber', 'item'],
	['customerOrderReference', 'reference'],
	['documentLines.customerOrderReference', 'reference'],
	['documentLines.customerOrderItemReference', 'item'],
	['supplierOrderReference', 'reference'],
	['documentLines.supplierOrderReference', 'reference'],
	['documentLines.supplierOrderReferenceItem', 'item'],
	['documentLines.millOrderReference', 'reference'],
	['documentLines.millOrderReferenceItem', 'item'],
	['supplierRFQNumber', 'reference'],
	['supplierRFQItemNumber', 'item'],
	['ebusinessOrderNumber', 'reference'],
	['ebusinessOrderItemNumber', 'item']
]);

const dimensionProperties = <const> ['thickness', 'width', 'length'];
export function createDimensionFilter(filterModel?: DimensionFilter) {
	if (!filterModel) {
		return undefined;
	}
	let criterionFilterValueNode: FieldExpression | NodeExpression | undefined;
	for (const key of dimensionProperties) {
		const value = filterModel[key];
		if (!value) {
			continue;
		}
		const search = term.escape(value);
		criterionFilterValueNode = criterionFilterValueNode !== undefined
			? {
				left: {
					field: key,
					term: search,
					quoted: false,
				},
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			}
			: {
				field: key,
				term: search,
				quoted: false,
			};
	}
	return criterionFilterValueNode;
}

const shipmentDimensionProperties =
	<const> ['thicknessDimensionConcat', 'widthDimensionConcat', 'lengthDimensionConcat'];
export function createShipmentDimensionFilter(filterModel?: ShipmentDimensionFilter) {
	if (!filterModel) {
		return undefined;
	}
	let criterionFilterValueNode: FieldExpression | NodeExpression | undefined;
	for (const key of shipmentDimensionProperties) {
		const value = filterModel[key];
		if (!value) {
			continue;
		}
		const search = term.escape(value);
		criterionFilterValueNode = criterionFilterValueNode !== undefined
			? {
				left: {
					field: key,
					term: `*${ term.escape(search.trim()) }*`,
					quoted: false,
				},
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			}
			: {
				field: key,
				term: `*${ term.escape(search.trim()) }*`,
				quoted: false,
			};
	}
	return criterionFilterValueNode;
}

const producedDimensionProperties =
	<const>['producedThickness', 'producedWidth', 'producedLength'];
export function createProducedDimensionFilter(filterModel?: ProducedDimensionFilter) {
	if (!filterModel) {
		return undefined;
	}
	let criterionFilterValueNode: FieldExpression | NodeExpression | undefined;
	for (const key of producedDimensionProperties) {
		const value = filterModel[key];
		if (!value) {
			continue;
		}
		const search = term.escape(value);
		criterionFilterValueNode = criterionFilterValueNode !== undefined
			? {
				left: {
					field: key,
					term: search,
					quoted: false,
				},
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			}
			: {
				field: key,
				term: search,
				quoted: false,
			};
	}
	return criterionFilterValueNode;
}

export function createReferenceItemFilter(filterModel: OrderNumberFilter, fields: string[] | undefined) {
	let criterionFilterValueNode: FieldExpression | NodeExpression | undefined;
	if (!fields) {
		return undefined;
	}
	for (const field of fields) {
		const key = FILTER_COLUMN_MAPPING.get(field);
		if (!key || !filterModel[key]) {
			continue;
		}
		const value = filterModel[key];
		if (typeof value !== 'string') {
			continue;
		}
		const search = key === 'item' ? term.escape(value) : `*${ term.escape(value) }*`;
		criterionFilterValueNode = criterionFilterValueNode !== undefined
			? {
				left: {
					field,
					term: search,
					quoted: false,
				},
				operator: 'AND',
				right: criterionFilterValueNode,
				parenthesized: false
			}
			: {
				field,
				term: search,
				quoted: false,
			};
	}
	return criterionFilterValueNode;
}

export function createDocumentReferenceItemFilter(filterModel: OrderNumberFilter, fields: string[] | undefined) {
	let criterionFilterValueNode: LuceneExpression | undefined;
	if (!fields) {
		return undefined;
	}
	let documentReferenceExp: FieldExpression | undefined;
	let documentLineReferenceExp: FieldExpression | undefined;
	let itemExp: FieldExpression | undefined;
	for (const field of fields) {
		const key = FILTER_COLUMN_MAPPING.get(field);
		if (!key || !filterModel[key]) {
			continue;
		}
		const value = filterModel[key];
		if (typeof value !== 'string') {
			continue;
		}
		if (key === 'reference') {
			if (!field.startsWith('documentLines')) {
				documentReferenceExp = {
					field,
					term: `*${ term.escape(value) }*`,
					quoted: false
				};
			} else {
				documentLineReferenceExp = {
					field,
					term: `*${ term.escape(value) }*`,
					quoted: false
				};
			}
		} else if (key === 'item') {
			itemExp = {
				field,
				term: value,
				quoted: false
			};
		}
		if (itemExp) {
			let documentLuceneExp: LuceneExpression | undefined;
			if (documentReferenceExp) {
				documentLuceneExp = {
					left: documentReferenceExp,
					operator: 'AND',
					parenthesized: true,
					right: itemExp
				};
			}

			let documentLineLuceneExp: LuceneExpression | undefined;
			if (documentLineReferenceExp) {
				documentLineLuceneExp = {
					left: documentLineReferenceExp,
					operator: 'AND',
					parenthesized: true,
					right: itemExp
				};
			}

			if (documentLuceneExp && documentLineLuceneExp) {
				criterionFilterValueNode = {
					left: documentLuceneExp,
					operator: 'OR',
					parenthesized: true,
					right: documentLineLuceneExp
				};
			} else if (documentLuceneExp) {
				criterionFilterValueNode = documentLuceneExp;
			} else if (documentLineLuceneExp) {
				criterionFilterValueNode = documentLineLuceneExp;
			} else {
				criterionFilterValueNode = itemExp;
			}
		} else if (documentReferenceExp && documentLineReferenceExp) {
			criterionFilterValueNode = {
				left: documentReferenceExp,
				operator: 'OR',
				parenthesized: true,
				right: documentLineReferenceExp
			};
		} else if (documentReferenceExp) {
			criterionFilterValueNode = documentReferenceExp;
		} else if (documentLineReferenceExp) {
			criterionFilterValueNode = documentLineReferenceExp;
		}
	}
	return criterionFilterValueNode;
}

export function createNumberFilter(filterModel: NumberFilter, field: string | undefined) {
	const isProduced = field === 'producedLength';
	const convert = (v?: number): string => {
		if (v === undefined || !Number.isFinite(v)) {
			return '*';
		}
		const scaled = isProduced ? v * 1000 : v;
		return String(scaled);
	};
	const min = convert(filterModel.moreThan);
	const max = convert(filterModel.lessThan);
	const node = {
		field,
		term_min: min || '*',
		term_max: max || '*',
		inclusive: 'both',
	};
	if (field === 'producedLength') {
		const formClause = { field: 'form', term: '04', quoted: false };
		return {
			left: formClause,
			operator: 'AND',
			right: node,
			parenthesized: false
		};
	}
	return node;
}

export function createExactNumberFilter(filterValue: ExactNumberFilter, field: string | undefined) {
	return {
		field,
		term: filterValue.value,
		quoted: false,
	};
}

export function createDateFilter(filterModel: Date | string, field: string | undefined) {
	return {
		field,
		term: filterModel,
		quoted: false,
	};
}

export function createDocumentDateFilter(filterModel: Date | string, fields: string[] | undefined) {
	let criterionFilterValueNode;
	if (!fields) {
		return undefined;
	}
	for (const field of fields) {
		criterionFilterValueNode = criterionFilterValueNode !== undefined
			? {
				left: createDateFilter(filterModel, field),
				operator: 'OR',
				right: criterionFilterValueNode,
				parenthesized: true
			}
			: createDateFilter(filterModel, field);
	}
	return criterionFilterValueNode;
}

export function createDateRangeFilter(filterModel: DateRangeFilter, field: string | undefined) {
	return {
		field,
		term_min: filterModel.from ? moment(filterModel.from).format(DATE_FORMAT_PATTERN) : '*',
		term_max: filterModel.to ? moment(filterModel.to).format(DATE_FORMAT_PATTERN) : '*',
		inclusive: 'both'
	};
}

function createFreeTextLuceneExpression(filterValue: string, field: string | undefined) {
	return {
		field,
		term: `*${ term.escape(filterValue.trim()) }*`,
		quoted: false,
	};
}

function createTextArrayFilter(searchText: string, field: string | undefined) {
	let criterionFilterValueNode;
	if (searchText && field) {
		for (const filterValue of searchText.split(';')) {
			criterionFilterValueNode = criterionFilterValueNode !== undefined
				? {
					left: createFreeTextLuceneExpression(filterValue, field),
					operator: 'OR',
					right: criterionFilterValueNode,
					parenthesized: true
				}
				: createFreeTextLuceneExpression(filterValue, field);
		}
	}
	return criterionFilterValueNode;
}

export function createDocumentDateRangeFilter(filterModel: DateRangeFilter, fields: string[] | undefined) {
	let criterionFilterValueNode: LuceneExpression | undefined;
	if (!fields) {
		return undefined;
	}
	let documentReferenceExp: RangeExpression | undefined;
	let documentLineReferenceExp: RangeExpression | undefined;
	for (const field of fields) {
		if (!field.startsWith('documentLines')) {
			documentReferenceExp = {
				field,
				term_min: filterModel.from ? moment(filterModel.from).format(DATE_FORMAT_PATTERN_ZONE) : '*',
				term_max: filterModel.to ? moment(filterModel.to).format(DATE_FORMAT_PATTERN_ZONE) : '*',
				inclusive: 'both'
			};
		} else {
			documentLineReferenceExp = {
				field,
				term_min: filterModel.from ? moment(filterModel.from).format(DATE_FORMAT_PATTERN_ZONE) : '*',
				term_max: filterModel.to ? moment(filterModel.to).format(DATE_FORMAT_PATTERN_ZONE) : '*',
				inclusive: 'both'
			};
		}
		if (documentReferenceExp && documentLineReferenceExp) {
			criterionFilterValueNode = {
				left: documentReferenceExp,
				operator: 'OR',
				parenthesized: true,
				right: documentLineReferenceExp
			};
		} else if (documentReferenceExp) {
			criterionFilterValueNode = documentReferenceExp;
		} else if (documentLineReferenceExp) {
			criterionFilterValueNode = documentLineReferenceExp;
		}
	}
	return criterionFilterValueNode;
}

export function createTextFilter(filterModel: TextFilter, field: string | undefined) {
	const text = filterModel.searchText?.trim() ?? '';
	const node = text.split(';').length > 0
		? createTextArrayFilter(text, field)
		: {
			field,
			term: `*${ term.escape(text) }*`,
			quoted: false,
		};
	if (field === 'producedLengthDimensionConcat') {
		const formClause = { field: 'form', term: '04', quoted: false };
		return {
			left: formClause,
			operator: 'AND',
			right: node,
			parenthesized: false
		};
	}
	return node;
}

function createShipmentMultiValueFieldExpression(
	field: string | undefined,
	value: string,
) {
	if (!field) {
		return undefined;
	}

	const escapedValue = term.escape(value.trim());
	if (!escapedValue) {
		return undefined;
	}

	const wildcardMatch: FieldExpression = {
		field,
		term: `*${ escapedValue }*`,
		quoted: false,
	};

	return wildcardMatch;
}

export function createShipmentMultiValueTextFilter(filterModel: TextFilter, field: string | undefined) {
	const text = filterModel.searchText?.trim() ?? '';
	let criterionFilterValueNode: FieldExpression | NodeExpression | undefined;

	for (const filterValue of text.split(';').map(value => value.trim()).filter(Boolean)) {
		const valueFilterNode = createShipmentMultiValueFieldExpression(field, filterValue);
		if (!valueFilterNode) {
			continue;
		}
		criterionFilterValueNode = criterionFilterValueNode
			? {
				left: valueFilterNode,
				operator: 'OR',
				right: criterionFilterValueNode,
				parenthesized: true,
			}
			: valueFilterNode;
	}

	if (field === 'producedLengthDimensionConcat' && criterionFilterValueNode) {
		const formClause = { field: 'form', term: '04', quoted: false };
		return {
			left: formClause,
			operator: 'AND',
			right: criterionFilterValueNode,
			parenthesized: false
		};
	}

	return criterionFilterValueNode;
}

export function createShipmentMultiValueNumberFilter(filterModel: NumberFilter, field: string | undefined) {
	return createNumberFilter(filterModel, field);
}

export function createShipmentMultiValueDateFilter(filterModel: Date | string, field: string | undefined) {
	if (typeof filterModel !== 'string') {
		return createDateFilter(filterModel, field);
	}

	return createShipmentMultiValueFieldExpression(field, filterModel);
}

export function createDocumentTextFilter(filterModel: TextFilter, fields: string[] | undefined) {
	let criterionFilterValueNode: FieldExpression | NodeExpression | undefined;
	if (!fields) {
		return undefined;
	}
	for (const field of fields) {
		const searchPhrase = `*${ term.escape(filterModel.searchText ?? '') }*`;
		criterionFilterValueNode = criterionFilterValueNode !== undefined
			? {
				left: {
					field,
					term: searchPhrase,
					quoted: false,
				},
				operator: 'OR',
				right: criterionFilterValueNode,
				parenthesized: true
			}
			: {
				field,
				term: searchPhrase,
				quoted: false,
			};
	}
	return criterionFilterValueNode;
}

function createFilter(field: string, value: string, applyLikeCondition: boolean) {
	return {
		field,
		term: applyLikeCondition ? `*${ value }*` : value,
		quoted: !applyLikeCondition,
	};
}

function createNoValueDropDownFilter(field: string, shouldApplyNotNullCondition?: boolean) {
	const notNullCondition = {
		field: `(!${ field }`,
		term: '*)',
		quoted: false,
	};
	const emptyCondition = {
		field: `(${ field }`,
		term: '"")',
		quoted: false,
	};
	return shouldApplyNotNullCondition ? {
		left: notNullCondition,
		operator: 'OR',
		right: emptyCondition,
		parenthesized: true,
	} : emptyCondition;
}

export function createDropDownFilter(
	filterModel: string,
	field: string | undefined,
	multiFilterCodes?: Map<string, string[]>,
	applyLikeCondition?: boolean,
	shouldApplyNotNullCondition?: boolean
) {
	// 'userValidationStatus' is a frontend-only code for the User Datacenter status filter,
	// translated to personStatus:"pend" AND status:"activ".
	if (filterModel === 'userValidationStatus') {
		return {
			left: createFilter('personStatus', 'pend', false),
			operator: 'AND',
			right: createFilter('status', 'activ', false),
			parenthesized: true
		};
	}
	if (!field) {
		return '';
	}
	if (field && (filterModel === 'novalue' || filterModel.trim() === '')) {
		return createNoValueDropDownFilter(field, shouldApplyNotNullCondition);
	}
	if (multiFilterCodes?.has(filterModel)) {
		const values = multiFilterCodes.get(filterModel);
		if (values) {
			let criterionFilterValueNode: FieldExpression | NodeExpression | undefined;
			for (const value of values) {
				const filter = createFilter(field, value, applyLikeCondition ?? false);
				criterionFilterValueNode = criterionFilterValueNode !== undefined
					? {
						left: filter,
						operator: 'OR',
						right: criterionFilterValueNode,
						parenthesized: true
					}
					: filter;
			}
			return criterionFilterValueNode;
		}
	}
	return createFilter(field, filterModel, applyLikeCondition ?? false);
}

export function createShipmentMultiValueDropDownFilter(
	filterModel: string,
	field: string | undefined,
	multiFilterCodes?: Map<string, string[]>,
	shouldApplyNotNullCondition?: boolean
) {
	return createDropDownFilter(
		filterModel,
		field,
		multiFilterCodes,
		false,
		shouldApplyNotNullCondition,
	);
}

export function createSuggestiveListFilter(filterValue: string, field: string | undefined) {
	return {
		field,
		term: filterValue,
		quoted: true,
	};
}

export function createDocumentMillDropDownFilter(filterModel: string, fields: string[] | undefined) {
	if (!fields) {
		return undefined;
	}
	// Normalize filterModel to an array of strings
	const values: string[] = Array.isArray(filterModel)
		? filterModel
		: filterModel.includes(',')
			? filterModel.split(',').map(v => v.trim()).filter(Boolean)
			: [filterModel];
	const groupedExpressions: (FieldExpression | NodeExpression)[] = [];

	for (const value of values) {
		const fieldExpressions: FieldExpression[] = fields.map(field => ({
			field,
			term: value,
			quoted: false
		}));
		// Combine field expressions for a single value with OR
		let group: FieldExpression | NodeExpression = fieldExpressions[0];
		for (let i = 1; i < fieldExpressions.length; i++) {
			group = {
				left: group,
				operator: 'OR',
				right: fieldExpressions[i],
				parenthesized: true
			};
		}
		groupedExpressions.push(group);
	}
	// Combine all grouped expressions with OR
	let finalExpression: FieldExpression | NodeExpression = groupedExpressions[0];
	for (let i = 1; i < groupedExpressions.length; i++) {
		finalExpression = {
			left: finalExpression,
			operator: 'OR',
			right: groupedExpressions[i],
			parenthesized: true
		};
	}
	return finalExpression;
}
export interface FilterInstance {
	getModel(): { customFilterName: FilterType } | undefined | null;
}
